import { IContentSection } from '../interfaces/card-info.interface';

// Long-form SEO content for /aeo-marketing-services, grouped by background band.
// Headings use the site convention: lowercase with <span class="gradient-text"> accents.
// Brand names and acronyms (ChatGPT, AI, Google AI Overviews, GEO, AEO) keep their casing.

// Band 1 (background-1): below the hero — AEO intro, ChatGPT optimization, entity optimization.
export const AEO_SECTIONS_INTRO: IContentSection[] = [
  {
    headingHtml: 'answer engine<br><span class="gradient-text">optimization</span><br>services',
    lead: [
      'Answer Engine Optimization (AEO) is the process of increasing your brand’s visibility within AI-powered answer engines such as ChatGPT, Google AI Overviews, Perplexity, Claude, and Gemini. Unlike traditional SEO, which focuses on ranking web pages, AEO focuses on becoming the answer that AI systems recommend when users ask questions.',
    ],
    outro: [
      'As AI-powered search continues to replace traditional search behavior, businesses must optimize not only for search engines but also for large language models, retrieval systems, and answer engines that synthesize information from multiple sources. SemNexus helps businesses build the authority, structure, and digital presence necessary to become trusted AI citations and recommendations across today’s leading AI platforms.',
    ],
  },
  {
    headingHtml: 'ChatGPT optimization & <span class="gradient-text">AI search visibility</span>',
    lead: [
      'Millions of users now ask ChatGPT for recommendations instead of searching through traditional search results.',
      'When users ask questions such as:',
    ],
    bullets: [
      'What is the best app marketing agency?',
      'Which CRM should I use?',
      'What is the best AI software?',
      'Who are the top app developers?',
    ],
    outro: [
      'The AI determines which brands deserve to be cited and recommended.',
      'Our ChatGPT optimization services help businesses improve visibility within AI-generated answers by strengthening authority signals, entity recognition, structured content, and third-party validation across the web. The goal is simple: increase the probability that AI systems recognize your company as a trusted answer.',
    ],
  },
  {
    headingHtml: 'entity optimization<br><span class="gradient-text">&amp; knowledge graph</span><br>development',
    lead: [
      'Modern AI systems think in entities, not keywords.',
      'An entity can be a company, product, founder, service, or organization. Before AI systems can confidently recommend a brand, they must first understand that the brand exists, what it does, and how it relates to other entities across the web.',
      'Our entity optimization services focus on:',
    ],
    bullets: [
      'Knowledge graph development',
      'Entity mapping',
      'Brand disambiguation',
      'Digital footprint expansion',
      'Entity relationship building',
      'AI identity verification',
    ],
    outro: [
      'This process helps establish your company as a recognized and authoritative entity across AI search ecosystems.',
    ],
  },
];

// Band 2 (background-2): AI citation building, schema/structured data, Google AI Overviews.
export const AEO_SECTIONS_TECH: IContentSection[] = [
  {
    headingHtml: '<span class="gradient-text">AI citation</span><br>building services',
    lead: [
      'Answer engines are designed to minimize hallucinations and increase factual accuracy. To accomplish this, they evaluate information from multiple sources and look for consensus across the internet.',
      'Our AI citation building services focus on increasing your brand’s visibility across trusted sources that influence AI retrieval systems, including:',
    ],
    bullets: [
      'Industry publications',
      'News websites',
      'Review platforms',
      'Professional directories',
      'Podcasts',
      'Forums',
      'Expert interviews',
      'Thought leadership content',
    ],
    outro: [
      'The more authoritative and consistent the signals surrounding your business, the more likely AI systems are to trust and reference your brand.',
    ],
  },
  {
    headingHtml: 'schema markup<br><span class="gradient-text">&amp; structured data</span><br>optimization',
    lead: [
      'Artificial intelligence systems process structured data far more efficiently than unstructured content. Advanced schema markup helps answer engines understand your products, services, organization, pricing, reviews, and expertise.',
      'Our technical AEO services include:',
    ],
    bullets: [
      'Organization schema',
      'FAQ schema',
      'Product schema',
      'Service schema',
      'Software schema',
      'Article schema',
      'Review schema',
      'Knowledge graph schema',
    ],
    outro: [
      'By improving machine readability, we help answer engines retrieve and understand your content more effectively.',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">Google AI Overviews</span> optimization',
    lead: [
      'Google’s AI Overviews have fundamentally changed search behavior by generating answers directly within search results. Ranking organically is no longer enough. Businesses must also earn inclusion within AI-generated summaries and recommendations.',
    ],
    outro: [
      'Our Google AI Overviews optimization services focus on creating content structures, authority signals, entity relationships, and technical implementations designed to improve visibility within Google’s AI-generated search experiences.',
    ],
  },
];

// Band 3 (background-3): GEO, reporting, and the bottom SEO block.
export const AEO_SECTIONS_GROWTH: IContentSection[] = [
  {
    headingHtml: '<span class="gradient-text">generative engine</span><br>optimization (GEO)',
    lead: [
      'Generative Engine Optimization (GEO) expands beyond traditional search engine optimization by focusing specifically on how generative AI systems retrieve, evaluate, and synthesize information. GEO and AEO share many similarities, but GEO focuses more broadly on visibility across the entire AI ecosystem.',
      'Our GEO services help businesses establish authority across:',
    ],
    bullets: [
      'ChatGPT',
      'Perplexity',
      'Claude',
      'Gemini',
      'Google AI',
      'Future AI search platforms',
    ],
    outro: [
      'By building comprehensive AI authority, businesses position themselves for long-term visibility as search continues evolving.',
    ],
  },
  {
    headingHtml: 'AEO reporting &amp; <span class="gradient-text">AI<br>visibility tracking</span>',
    lead: [
      'Traditional SEO measures rankings. AEO measures recommendations, citations, visibility, and share of voice across answer engines.',
      'Our reporting framework tracks:',
    ],
    bullets: [
      'AI citations',
      'Brand mentions',
      'Competitive visibility',
      'Answer engine presence',
      'Share of voice',
      'Entity growth',
      'Authority development',
    ],
    outro: [
      'This allows businesses to monitor how AI systems perceive and reference their brand over time.',
    ],
  },
  {
    headingHtml: 'why choose <span class="gradient-text">semnexus</span> as your AEO agency',
    lead: [
      'Answer Engine Optimization requires a fundamentally different approach than traditional SEO. Success depends on building machine-readable authority, strengthening entity recognition, improving structured data, earning trusted third-party citations, and creating content specifically designed for AI retrieval systems.',
    ],
    outro: [
      'SemNexus combines technical optimization, AI search strategy, entity development, citation building, and content architecture into a unified AEO framework designed to help businesses become the trusted answer across ChatGPT, Google AI Overviews, Perplexity, Claude, Gemini, and future AI search platforms. As answer engines continue replacing traditional search behavior, our goal is simple: position your brand where AI systems look first and recommend most often.',
    ],
  },
];
